import React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useOrgContext } from '@/contexts/OrgContext';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from "sonner"

type OrgUserRole = 'USER' | 'ADMIN';

interface OrgAdminEditRoleProps {
  orgUserId: string;
  initRole: OrgUserRole;
}

export function OrgAdminEditRole({ orgUserId, initRole }: OrgAdminEditRoleProps) {
  const { organisation } = useOrgContext();
  const queryClient = useQueryClient();

  const { mutate: updateRole, isPending } = useMutation({
    mutationFn: async (newRole: OrgUserRole) => {
      const response = await fetch(
        `/api/org/${organisation.id}/users/${orgUserId}/role`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ role: newRole }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to update user role');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizationUsers', organisation.id] });
      toast.success('User role updated successfully');
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to update user role');
    },
  });

  return (
    <Select
      defaultValue={initRole}
      disabled={isPending}
      onValueChange={(value: OrgUserRole) => updateRole(value)}
    >
      <SelectTrigger className="w-[110px]">
        <SelectValue placeholder="Select role" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="USER">User</SelectItem>
        <SelectItem value="ADMIN">Admin</SelectItem>
      </SelectContent>
    </Select>
  );
}
